import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError, combineLatest } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import { MeteringApiService } from '@shure/shared/angular/data-access/device-metering';
import {
	ApolloQueryErrorMapper,
	ApolloQueryError,
	AudioChannelFeatureUpdateInput,
	NodeUpdateInput
} from '@shure/shared/angular/data-access/system-api/core';
import { mapChannelLoudspeakerToSpeakerTypeSysApi } from '@shure/shared/angular/data-access/system-api/mappers';
import {
	ChannelAnalogLevel,
	InputSource,
	ChannelOutputPath,
	ChannelLoudspeaker
} from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { DspDevice, DspDeviceChannel, DspDeviceChannelMetering } from '../../api/models';
import { ChannelInfoApiService } from '../../api/services';
import { GetChannelQueryGQL, UpdateChannelMutationGQL } from '../graphql/generated/channel-info-channel.generated';
import { GetDeviceQueryGQL } from '../graphql/generated/channel-info-device.generated';
import { mapChannelInfoChannelFromSysApi, mapChannelInfoDeviceFromSysApi } from '../mappers';

import { DaiChannelInfoSubscriptionsService } from './';

@Injectable()
export class DaiChannelInfoApiService implements ChannelInfoApiService {
	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		private readonly getDeviceQueryGQL: GetDeviceQueryGQL,
		private readonly getChannelQueryGQL: GetChannelQueryGQL,
		private readonly updateChannelMutationGQL: UpdateChannelMutationGQL,
		private readonly subscriptions: DaiChannelInfoSubscriptionsService,
		private readonly meteringService: MeteringApiService,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService
	) {
		this.logger = logger.createScopedLogger('DaiChannelInfoApiService');
	}

	public getDevice$(deviceId: string): Observable<DspDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying device', { queryId, deviceId });
		return combineLatest([
			this.getDeviceQueryGQL.watch({ id: deviceId }, { errorPolicy: 'all', fetchPolicy: 'cache-and-network' })
				.valueChanges,
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId)
		]).pipe(
			map(([query, auth]) => {
				if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const device = mapChannelInfoDeviceFromSysApi(query.data.node, auth.authenticated, auth.readonlyMode);
				this.logger.trace('getDevice$()', 'Query updated', { device });
				return device;
			}),
			tapOnce((device) => {
				this.subscriptions.device.subscribe(queryId, device.id);
				this.subscriptions.equalizer.subscribeToOnly(queryId, device.equalizerFilterIds);
			}),
			noMoreSubscribers(() => {
				this.subscriptions.device.unsubscribeQuery(queryId);
				this.subscriptions.equalizer.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device', {
					deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getChannel$(channelId: string): Observable<DspDeviceChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying channel', { queryId, channelId });
		return this.getChannelQueryGQL.watch({ id: channelId }, { errorPolicy: 'all' }).valueChanges.pipe(
			map((query) => {
				if (!query.data?.node || !('isAudioChannelNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const channel = mapChannelInfoChannelFromSysApi(query.data.node);
				this.logger.trace('getChannel$()', 'Query updated', { channel });
				return channel;
			}),
			tapOnce((channel) => {
				this.subscriptions.channels.subscribe(queryId, channel.id);
				if (channel.features.rfChannel) {
					this.subscriptions.rfChannels.subscribe(queryId, channel.features.rfChannel.id);
				}
			}),
			noMoreSubscribers(() => {
				this.subscriptions.channels.unsubscribeQuery(queryId);
				this.subscriptions.rfChannels.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getChannel$()', 'Failed to query channel', {
					channelId,
					error
				});

				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getChannelMetering$(channelId: string, isVirtual: boolean): Observable<DspDeviceChannelMetering> {
		return this.meteringService.subscribeChannelMetering$(channelId, isVirtual).pipe(
			map((metering) => ({
				leftOrMonoPeak: metering.features.audioPeak?.peak ?? -999,
				leftOrMonoValue: metering.features.audioRms?.rms ?? -999
			}))
		);
	}

	public getStereoChannelMetering$(channelId: string, isVirtual: boolean): Observable<DspDeviceChannelMetering> {
		return this.meteringService.subscribeStereoChannelMetering$(channelId, isVirtual).pipe(
			map((metering) => ({
				leftOrMonoPeak: metering.features.audioPeak?.leftPeak ?? -999,
				leftOrMonoValue: metering.features.audioRms?.leftRms ?? -999,
				rightPeak: metering.features.audioPeak?.rightPeak ?? -999,
				rightValue: metering.features.audioRms?.rightRms ?? -999
			}))
		);
	}

	public setChannelName(channelId: string, name: string): Observable<void> {
		return this.updateChannel(channelId, [{ name: { name } }]);
	}

	public setChannelMute(channelId: string, muted: boolean): Observable<void> {
		return this.updateChannel(channelId, [{ mute: { muted } }]);
	}

	public setChannelGain(channelId: string, gain: number): Observable<void> {
		return this.updateChannel(channelId, [{ gain: { gain } }]);
	}

	public setChannelAnalogLevel(channelId: string, level: ChannelAnalogLevel): Observable<void> {
		return this.updateChannel(channelId, [{ analogLevel: { level: level } }]);
	}

	public setChannelAutoMute(channelId: string, enabled: boolean): Observable<void> {
		return this.updateChannel(channelId, [{ autoMute: { enabled } }]);
	}

	public setPcPeripheral(channelId: string, selectedPeripheralId: string): Observable<void> {
		return this.updateChannel(channelId, [{ pcPeripheral: { selectedPeripheral: { id: selectedPeripheralId } } }]);
	}

	public setInputSource(channelId: string, inputSource: InputSource): Observable<void> {
		return this.updateChannel(channelId, [{ inputSource: { source: inputSource } }]);
	}

	public setOutputPath(channelId: string, outputPath: ChannelOutputPath): Observable<void> {
		return this.updateChannel(channelId, [{ outputPath: { path: outputPath } }]);
	}

	public setPassiveSpeakerType(channelId: string, type: ChannelLoudspeaker): Observable<void> {
		return this.updateChannel(channelId, [
			{ passiveSpeakers: { type: mapChannelLoudspeakerToSpeakerTypeSysApi(type) } }
		]);
	}

	private updateChannel(channelId: string, updates: AudioChannelFeatureUpdateInput[]): Observable<void> {
		const inputs = this.mapChannelUpdateToNodeInputs(channelId, updates);

		this.logger.trace('updateChannel()', 'Updating channel', { channelId, updates, inputs });
		return this.mutateChannels(inputs);
	}

	/**
	 * Perform channel mutation.
	 */
	private mutateChannels(updates: NodeUpdateInput[]): Observable<void> {
		this.logger.trace('mutateChannel()', 'Updating channel', { updates });
		return this.updateChannelMutationGQL
			.mutate({ updates, injectKeyring: '//supplied-by-keyring-injector' }, { errorPolicy: 'all' })
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						throw result.data.updateNodes[0].error.message;
					}

					this.logger.trace('mutateChannel()', 'Done');
					return undefined;
				}),
				catchError((error: Error) => {
					this.logger.error('mutateChannel()', 'Error', { error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	/**
	 * Map channel update(s) to node input.
	 */
	private mapChannelUpdateToNodeInputs(
		channelId: string,
		updates: AudioChannelFeatureUpdateInput[]
	): NodeUpdateInput[] {
		return updates.reduce<NodeUpdateInput[]>(
			(all, next) => [...all, { audioChannel: { id: channelId, features: next } }],
			[]
		);
	}
}
